import React, { useMemo, useState } from 'react';
import { Flex, Pagination } from 'antd';
import { faCommentLines } from '@fortawesome/pro-regular-svg-icons';
import { capitalize, debounce } from 'lodash-es';
import { useParams } from 'react-router-dom';

import { trackEvent } from 'jf/analytics/Analytics';
import {
  StudyScoreClient,
  TextualResponse,
  TextualSummary,
  TopicVerbatimWithTeamRef,
} from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { AISummary } from 'jf/pages/studyAnalyzer/topicDrawer/AISummary';
import { useClientQuery } from 'jf/utils/useClientQuery';

import { DevExCard } from './DevExCard';
import { DevExEmpty } from './DevExEmpty';
import { DevExSearch } from './DevExSearch';
import { DevExTag } from './DevExTag';
import { DevExText } from './DevExText';

const SHOW_COUNT = 4;

export const DevExAISummaryCard = ({
  header,
  summary,
  items,
  topicSlug,
  eventType,
  title,
}: {
  header?: string;
  summary: TextualSummary | undefined;
  items: (TopicVerbatimWithTeamRef | TextualResponse)[];
  eventType: 'comments' | 'responses';
  topicSlug?: string;
  title: string;
}) => {
  const theme = useDevExTheme();
  const { studyRef } = useParams<{ studyRef: string }>();
  const { data: scoredTeams } = useClientQuery(StudyScoreClient.getStudyScoredTeams, {
    studyRef: studyRef,
  });
  const numItems = items.length;
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const startIndex = (currentPage - 1) * SHOW_COUNT;
  const endIndex = startIndex + SHOW_COUNT;
  const debounceTrackSearch = useMemo(() => {
    return debounce((responseLength, search) => {
      trackEvent(`survey-analyzer:${eventType}:search`, {
        search,
        results: responseLength,
        total: items?.length,
        topic: topicSlug || undefined,
      });
    }, 1000);
  }, [prompt]);
  const filteredItems: (TopicVerbatimWithTeamRef | TextualResponse)[] | undefined = useMemo(() => {
    const results = items.filter((comment) =>
      comment.responseText.toLowerCase().includes(searchQuery.toLowerCase())
    );
    if (searchQuery?.length) {
      debounceTrackSearch(results?.length, searchQuery);
    }
    return results;
  }, [items, searchQuery]);
  const filteredItemsByPage = filteredItems?.slice(startIndex, endIndex);
  return (
    <DevExCard style={{ paddingInline: theme.variable.spacing.lg }}>
      {!!header && (
        <DevExText
          style={{
            paddingTop: theme.variable.spacing.sm,
          }}
          lined
        >
          {header}
        </DevExText>
      )}
      <Flex vertical gap={theme.variable.spacing.lg}>
        {items && (
          <>
            {items.length > 0 ? (
              <>
                {window.dx.user?.company.canUseGenerativeAiFeatures && (
                  <AISummary
                    summary={summary?.summary}
                    summaryType={summary?.summaryType}
                    title={title}
                    eventType={eventType}
                  />
                )}
                <Flex align="center" justify="space-between">
                  <Flex gap={theme.variable.spacing.sm}>
                    <DevExText lined type="secondary">
                      {capitalize(eventType)}
                    </DevExText>
                    <DevExTag>{numItems} </DevExTag>
                  </Flex>
                  {items && numItems > 1 && (
                    <DevExSearch
                      placeholder="Search for a response"
                      searchQuery={searchQuery}
                      setSearchQuery={setSearchQuery}
                      collapsible
                    />
                  )}
                </Flex>
                <Flex vertical gap={theme.variable.spacing.sm}>
                  {filteredItems &&
                    filteredItems?.length > 0 &&
                    filteredItemsByPage?.map((response, index) => {
                      let team;

                      if (eventType === 'comments') {
                        // forced to cast response to access teamref
                        team = scoredTeams?.find(
                          (team) => team.ref === (response as TopicVerbatimWithTeamRef).teamRef
                        );
                      }
                      return (
                        <div key={index}>
                          <DevExText
                            style={{
                              paddingBottom: theme.variable.spacing.sm,
                              borderBottom: theme.color.border.secondary,
                            }}
                            lined
                          >
                            "{response.responseText}"
                          </DevExText>
                          {team && (
                            <div style={{ textAlign: 'end', color: theme.color.text.secondary }}>
                              — {team.levelName} {team.value}
                            </div>
                          )}
                          <hr
                            style={{
                              border: `1px solid` + theme.color.border.secondary,
                              borderRadius: theme.variable.borderRadius,
                            }}
                          />
                        </div>
                      );
                    })}
                </Flex>
                <Pagination
                  total={filteredItems.length}
                  pageSize={SHOW_COUNT}
                  onChange={(page) => {
                    trackEvent('study-analyzer:open-text-results:pagination', {
                      page,
                    });
                    setCurrentPage(page);
                  }}
                  // have to set this to false, otherwise will show page size changer if more than 50 items
                  showSizeChanger={false}
                  hideOnSinglePage
                  style={{ alignSelf: 'center' }}
                />
              </>
            ) : (
              <>
                <Flex gap={theme.variable.spacing.sm}>
                  <DevExText lined type="secondary">
                    {title}
                  </DevExText>
                  <DevExTag>{numItems} </DevExTag>
                </Flex>
                <DevExEmpty
                  icon={faCommentLines}
                  label="No respondents provided feedback when voting for this topic."
                />
              </>
            )}
          </>
        )}
      </Flex>
    </DevExCard>
  );
};
